/**
 * @fileoverview Real-geometry hero art (WEB-654, DPL-04).
 *
 * WEB-775 (Shaun Boehringer, 2026-08-17): **intentionally unmounted** from
 * the default `/discover` header. The header now paints
 * {@link DiscoverHeroStripBackdrop} — static decorative texture that is
 * never gated on a footprint fetch. This module stays exported, tested,
 * and available for any future surface where geographic accuracy actually
 * matters (neighborhood spotlight, map preview, etc.). Do not delete.
 *
 * A coordinate graticule overlaid with the city's real neighborhood
 * footprints — the same API-1657 geometry the row hoodmarks draw, projected
 * through one shared extent so the outlines sit in their true relative
 * positions. The art is the city, not a gradient blob.
 *
 * Three properties are load-bearing:
 * - **Decorative.** `aria-hidden` on the whole layer; it never enters the
 *   accessibility tree and carries no title/desc.
 * - **Zero layout influence.** Absolutely positioned, so the hero's measured
 *   height is identical whether it renders or not (CLS).
 * - **Honest absence.** No geometry means no layer. A generic shape here would
 *   be a placeholder claiming to be Charlotte.
 *
 * Static by construction: no animation loop, no per-frame work, and the paths
 * are the API's pre-simplified geometry rather than anything re-simplified
 * client-side.
 */

import * as React from 'react';

import { cn } from '@/components/atoms/primitives/utils';
import { buildCityGraticule } from '@/lib/neighborhoods/cityGraticule';
import type { CityFootprintProjection } from '@/lib/neighborhoods/footprints';

export interface DiscoverHeroStripArtProps {
  /**
   * City-wide footprint projection. `null`/`undefined` — still loading, the
   * fetch failed, or the payload carried no drawable geometry — renders
   * nothing.
   */
  readonly projection?: CityFootprintProjection | null;
  readonly className?: string;
}

/**
 * Masked left→right so the headline column keeps full contrast. A CSS mask
 * (rather than per-path opacity) keeps the falloff uniform across the
 * graticule and the footprints, so no single stroke can crowd the text.
 *
 * Applied to the art box, NOT the full-width layer: the art occupies only the
 * trailing slice of the hero, so a layer-wide ramp would spend its whole fade
 * on empty space and the art would begin at a hard vertical edge.
 */
const MASK_IMAGE =
  'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.28) 30%, rgba(0,0,0,0.72) 62%, black 100%)';

export function DiscoverHeroStripArt({
  projection,
  className,
}: DiscoverHeroStripArtProps): React.ReactElement | null {
  // Honest absence: no geometry, no layer — never a placeholder shape.
  if (!projection || projection.footprints.length === 0) return null;

  // Deliberately not memoised: this is a handful of arithmetic over ~20 lines,
  // and a hook here would force a `'use client'` boundary on a module that
  // {@link DiscoverHeroStrip} renders as a pure server component (FRE-887).
  const graticule = buildCityGraticule(projection.bbox);

  return (
    <div
      aria-hidden
      data-testid="discover-hero-strip-art"
      className={cn(
        // `-z-10` sits the art behind the hero's copy. The section owns the
        // stacking context, so this stays above the section's own gradient.
        'pointer-events-none absolute inset-0 -z-10 overflow-hidden',
        // DPL-29 reclaims the ≤720 fold — the art is the first thing to go.
        'hidden min-[721px]:block',
        className,
      )}
    >
      <div
        // Square box anchored to the trailing edge, overscanning the band just
        // enough to bleed past its edges without cropping the city: the whole
        // point is the arrangement, so the extent has to stay legible.
        className="absolute right-0 top-1/2 aspect-square h-[150%] -translate-y-1/2"
        style={{ maskImage: MASK_IMAGE, WebkitMaskImage: MASK_IMAGE }}
      >
        <svg
          viewBox="0 0 1 1"
          preserveAspectRatio="xMidYMid meet"
          className="size-full"
          focusable="false"
        >
          <g stroke="var(--accent)" strokeOpacity={0.24} strokeWidth={1}>
            {graticule.map((line) => (
              <line
                key={`${line.orientation}-${line.degrees}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                // `vector-effect` does NOT inherit, so it must sit on the drawn
                // element. On a `<g>` it silently does nothing and the 1-unit
                // stroke scales with the unit viewBox into a solid slab.
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>

          <g
            fill="var(--accent)"
            fillOpacity={0.08}
            stroke="var(--accent)"
            strokeOpacity={0.7}
            strokeWidth={1.25}
            strokeLinejoin="round"
          >
            {projection.footprints.map((footprint) => (
              // `evenodd` matches NeighborhoodFootprintMark: the boundary source
              // does not guarantee RFC 7946 ring winding, so `nonzero` would fill
              // interior rings solid.
              <path
                key={footprint.slug}
                d={footprint.path}
                fillRule="evenodd"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </g>
        </svg>
      </div>
    </div>
  );
}
